<template>
    <div v-if="isAddressSectionInScope" id="bene-address-details-container">
        <f7-list form class="form-group">
            <f7-row class="wandr-paragraph-1 vertical-margin">{{$t('beneficiary-address-details')}}</f7-row>

            <f7-list-input
                input-id     ="beneficiaryCountryId"
                id           ="beneficiaryCountryInput"
                name         ="beneficiaryCountryId"
                class        ="clickable-row use-chevron"

                :label       ="addressLabel('beneficiary-bene-country')"
                :placeholder ="$t('beneficiary-select-bene-country')"
                :value       ="beneficiaryCountryName"
                @change      ="changeBeneficiaryCountry($event.target.value); validateInput($event.target); setInput($event.target);"

                outline
                floating-label
            >
                <f7-link no-link-class popover-open=".popover-beneficiary-country" slot="content-start" class="clickable-item"></f7-link>
            </f7-list-input>

            <f7-popover id="dropdownBeneficiaryCountry" class="popover-beneficiary-country dropdown-with-image-2 dropdown beneficiary" vertical-position="bottom" :backdrop="true">
                <f7-list>
                    <f7-list-item popover-close link="#" v-for="c in allCountryList" :key="c.id" :selected="c.id == beneficiaryCountryId" @click="changeBeneficiaryCountry(c.id)">
                        {{c.name}}<img :src="getFlagImg(c.id)" class="ml-sm"/>
                    </f7-list-item>
                </f7-list>
            </f7-popover>

            <f7-list-input
                input-id     ="beneficiaryTownCity"
                name         ="beneficiaryTownCity"
                type         ="text"

                :label       ="addressLabel('beneficiary-town-city')"
                :placeholder ="$t('beneficiary-town-city')"
                :value       ="beneficiaryTownCity"
                @change      ="setBeneficiaryTownCity($event.target.value); validateInput($event.target); setInput($event.target);"

                autocomplete ="off"
                maxlength    ="100"
                outline
                floating-label
                clear-button
            ></f7-list-input>
        </f7-list>
    </div>
</template>

<script>
    // DR-1845 - Beneficiary Country / Town-City (AML screening). Shared by both
    // new-beneficiary-details.vue (create) and beneficiary-modify.vue (modify).
    // In modify, beneficiaryCountryId/beneficiaryTownCity are populated by the
    // parent page's own loadBeneficiaryDetails() from the beneficiary's
    // additionalFieldList (fieldId 39/37) - this component only renders the
    // (reactive) store values, it does not fetch them itself.
    import { mapGetters, mapActions } from "vuex";
    export default {
        props: {
        },
        async mounted() {
            // Refresh whether the field is mandatory for the current destination
            // country - covers direct/deep-linked entry into this page, where
            // beneficiary-select-country's own change handler never fires.
            if(this.homeCountryId > 0) {
                await this.loadAddressConfig(this.homeCountryId);
            }
            // DR-1845 - the Beneficiary Country dropdown offers every country, not
            // just this branch's remittance corridors; loadAllCountryList() no-ops
            // once already cached.
            await this.loadAllCountryList();

            // The Town/City input only exists in the DOM once isAddressSectionInScope
            // is true (v-if on the component root); init immediately if it already
            // is, otherwise the watcher below picks it up once it becomes true.
            if(this.isAddressSectionInScope) {
                this.$nextTick(() => { this.initCityAutocomplete(); });
            }
        },
        data: function() {
            return {
                autocompleteTownCity: null,
                citySuggestTimer: null,
                // DR-1845 - set right before change() writes the selected city name into
                // the input; source() checks it to skip firing a fresh citysuggest search
                // for that programmatic value-set, instead of treating it like the user
                // typing a new query.
                suppressCitySearch: false
            }
        },
        computed: {
            ...mapGetters('beneficiary', [
                'homeCountryId',
                'allCountryList',
                'beneficiaryCountryId',
                'beneficiaryCountryName',
                'beneficiaryTownCity',
                'addressHardMandatory',
                'isCountryAddressMandatory',
                'isAddressSectionInScope',
                'transactionTypeId',
            ]),
        },
        watch: {
            // The modify flow resolves isCountryAddressMandatory/beneficiaryCountryId
            // asynchronously (parent page's loadBeneficiaryDetails) - the Town/City
            // input, and hence the autocomplete target, may not exist yet on mounted().
            isAddressSectionInScope(newVal) {
                if(newVal && !this.autocompleteTownCity) {
                    this.$nextTick(() => { this.initCityAutocomplete(); });
                }
            }
        },
        on: {
            pageBeforeRemove() {
                if(this.autocompleteTownCity) {
                    this.autocompleteTownCity.destroy();
                }
                clearTimeout(this.citySuggestTimer);
            },
        },
        methods: {
            ...mapActions('beneficiary', [
                'setBeneficiaryCountry',
                'setBeneficiaryTownCity',
                'loadAddressConfig',
                'loadAllCountryList',
            ]),

            // DR-1845 - only show a "required" marker when BOTH the global toggle
            // and the destination country itself require it; otherwise the field
            // stays clearly optional (soft mode still lets staff/customer proceed).
            addressLabel(key) {
                const label = this.$t(key);
                return (this.addressHardMandatory && this.isCountryAddressMandatory) ? (label + ' *') : label;
            },

            changeBeneficiaryCountry(countryId) {
                const country = this.allCountryList.find(c => c.id == countryId);
                this.setBeneficiaryCountry({id: countryId, name: country ? country.name : ""});
            },

            // DR-1845 - GeoNames city typeahead, scoped to the selected Beneficiary
            // Country (not the destination corridor) - same server-side proxy
            // pattern retail uses. Framework7's built-in autocomplete widget
            // ('dropdown' mode keeps the input freely editable, matching retail:
            // the suggestion is optional, staff/customer can still type any value).
            initCityAutocomplete() {
                const self = this;
                const app  = self.$f7;

                self.autocompleteTownCity = app.autocomplete.create({
                    inputEl: '#beneficiaryTownCity',
                    openIn: 'dropdown',
                    closeOnSelect: true,
                    source(query, render) {
                        clearTimeout(self.citySuggestTimer);
                        // DR-1845 - after change() picks a suggestion, F7 writes the
                        // selected text back into the input, which can re-trigger this
                        // source() callback for that same programmatic value. Without this
                        // guard, that fires a second, needless citysuggest call using the
                        // just-selected text as the query (confirmed live: a selected
                        // label like "Kowloon (HK)" got re-sent as q=Kowloon+(HK), which
                        // broke CORS preflight against the production edge/proxy). Skip it
                        // once, then resume normal search behavior for actual typing.
                        if(self.suppressCitySearch) {
                            self.suppressCitySearch = false;
                            self.autocompleteTownCity.close();
                            return;
                        }
                        // DR-1845 - strip anything that isn't a letter (incl. accented,
                        // e.g. "São Paulo"/"Zürich"), digit, space, apostrophe or hyphen
                        // before it ever becomes part of the outbound request URL. The
                        // production WAF blocks "(word)"-shaped content regardless of
                        // URL-encoding (confirmed live: %28/%29-encoded "(HK)" still got
                        // rejected, since the WAF normalizes/decodes before pattern-
                        // matching) - stripping symbols client-side avoids depending on
                        // encoding to get past it, and is a reasonable constraint for a
                        // city-name search either way.
                        const sanitizedQuery = (query || '').replace(/[^\p{L}\p{N}\s'-]/gu, '');
                        if(!sanitizedQuery || sanitizedQuery.trim().length < 3) {
                            // DR-1845 - explicit close(), matching beneficiary-bt-bank-name.vue's
                            // proven-working pattern: F7's dropdown autocomplete doesn't reliably
                            // auto-open/close purely from render() on this mobile WebView (renders
                            // an empty box that never actually opens with content) - the bank name
                            // selector never hit this because it always calls open()/close()
                            // explicitly instead of relying on render() alone.
                            self.autocompleteTownCity.close();
                            return;
                        }
                        self.autocompleteTownCity.open();
                        self.citySuggestTimer = setTimeout(async () => {
                            const suggestions = await self.$api.staticData.getCitySuggestions(sanitizedQuery.trim(), self.beneficiaryCountryId);
                            const items = (suggestions || []).map(s => {
                                let label = s.name;
                                if(s.admin1) { label += ', ' + s.admin1; }
                                if(s.countryCode) { label += ' (' + s.countryCode + ')'; }
                                return label;
                            });
                            render(items);
                        }, 350);
                    },
                    on: {
                        // DR-1845 - F7's dropdown autocomplete sizes its max-height from
                        // the space it thinks is available below the input; on this long
                        // scrolling page (the section sits right above the submit button,
                        // near the very end of the page's content) it badly underestimates
                        // that - confirmed via direct inspection: still capped at 73px
                        // (~1 row) even with the pb-200 padding-bottom trick that fixes
                        // account-occupation.vue's dropdown. Overriding the inline
                        // max-height F7 sets is the only way that reliably shows more
                        // than one suggestion here.
                        opened() {
                            const dropdownEl = self.autocompleteTownCity.$dropdownEl;
                            const innerEl    = dropdownEl ? dropdownEl.find('.autocomplete-dropdown-inner') : null;
                            if(innerEl && innerEl.length) { innerEl.css('max-height', '260px'); }
                        },
                        change(selectedItems) {
                            // DR-1845 - set before writing the selected value below, so the
                            // resulting programmatic input change doesn't fire a fresh
                            // citysuggest search (see source()'s guard above).
                            // DR-1845 fix - the flag alone isn't enough: source() only checks
                            // it once, synchronously, at the top of the call, but the actual
                            // fetch fires 350ms later from a debounce timer that never
                            // re-checks it. If source() ran (and scheduled that timer) before
                            // change() got a chance to set the flag, setting it here afterward
                            // was too late - the already-scheduled request still fired
                            // (confirmed live: a search for "Kowloon HK" still went out right
                            // after selecting it). Cancel that pending timer directly too, so
                            // it's covered regardless of which of source()/change() runs first.
                            clearTimeout(self.citySuggestTimer);
                            self.suppressCitySearch = true;
                            // Selected label is "City[, Admin1][ (CC)]" - store only the city
                            // name, and correct the input's own value so a later native
                            // change/blur (if any) re-reads the clean value rather than the
                            // full label.
                            // DR-1845 fix - label.split(',')[0] alone only strips the Admin1
                            // part; when a suggestion has no admin1 (confirmed live for a
                            // Hong Kong "Kowloon" entry) the label is just "Kowloon (HK)" with
                            // no comma, so the old code stored the whole thing - "(HK)" and
                            // all - as the city name. That corrupted value then got re-sent
                            // as the query on the next citysuggest search (before the
                            // suppressCitySearch guard above existed), and the literal
                            // parentheses in that querystring broke CORS preflight on the
                            // live server. Strip the trailing " (CC)" suffix first,
                            // regardless of whether Admin1/a comma is present, then split on
                            // comma.
                            const label              = (selectedItems && selectedItems[0]) ? selectedItems[0] : '';
                            const withoutCountryCode = label.replace(/\s*\([A-Za-z]{2,3}\)\s*$/, '');
                            const cityName           = withoutCountryCode.split(',')[0].trim();
                            const inputEl            = document.getElementById('beneficiaryTownCity');
                            if(inputEl) { inputEl.value = cityName; }

                            self.setBeneficiaryTownCity(cityName);
                            // The store's generic validateInput dispatcher indexes
                            // inputEl.childNodes[0] for an earlier, unrelated branch
                            // before ever reaching ours - childNodes: [] keeps that
                            // safe for this plain (non-DOM) object.
                            self.validateInput({id: 'beneficiaryTownCity', value: cityName, childNodes: []});
                            self.setInput({id: 'beneficiaryTownCity', value: cityName, childNodes: []});

                            // DR-1845 - safety net: if source() never re-fires for this
                            // selection (so its own reset in the guard above never runs),
                            // don't leave the flag stuck true and silently block the next
                            // real search.
                            setTimeout(() => { self.suppressCitySearch = false; }, 500);
                        }
                    }
                });
            },

            validateInput(inputEl){
                this.$emit("validateInput", inputEl);
            },

            setInput(inputEl){
                this.$emit("setInput", inputEl);
            }
        }
    }
</script>
